import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import ObligationService from '@/shared/services/ObligationService';
import NotificationService from '@/shared/services/NotificationService';
import { scheduleNotificationCountRefresh } from '@/shared/utils/notificationCountSync';

/**
 * Zajedničke akcije za jedno obaveštenje (pročitano, moderator accept/reject).
 *
 * @param {object} notification - Obaveštenje sa BE (`id`, `related_entity_id`, …).
 * @param {{ onResolved?: (notificationId: string) => void, onModeratorAssignmentChanged?: (obligationId: string) => Promise<void>|void }} [options]
 * @returns {{ busy: boolean, moderatorLoading: string|null, handleMarkRead: Function, handleAcceptModeration: Function, handleRejectModeration: Function }}
 */
export function useNotificationItemActions(notification, { onResolved, onModeratorAssignmentChanged } = {}) {
  const [busy, setBusy] = useState(false);
  const [moderatorLoading, setModeratorLoading] = useState(null);
  const relatedEntityId = notification?.related_entity_id;
  const notificationId = notification?.id != null ? String(notification.id) : null;

  const handleMarkRead = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!notificationId || busy || moderatorLoading) return;
      setBusy(true);
      try {
        await NotificationService.toggleRead(notificationId);
        toast.success('Означено као прочитано', { position: 'bottom-center' });
        scheduleNotificationCountRefresh();
        onResolved?.(notificationId);
      } catch (err) {
        toast.error(err?.message || 'Грешка при означавању', { position: 'bottom-center' });
      } finally {
        setBusy(false);
      }
    },
    [notificationId, busy, moderatorLoading, onResolved]
  );

  const handleAcceptModeration = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!relatedEntityId || !notificationId || moderatorLoading || busy) return;
      setModeratorLoading('accept');
      try {
        // X-Role: MODERATOR — definisano policy-jem u endpointRolePolicy.js (allowFunctionalModerator: true)
        await ObligationService.acceptModeration(relatedEntityId);
        await onModeratorAssignmentChanged?.(relatedEntityId);
        await NotificationService.toggleRead(notificationId);
        toast.success('Прихватили сте улогу moderatora.', { position: 'bottom-center' });
        scheduleNotificationCountRefresh();
        onResolved?.(notificationId);
      } catch (err) {
        toast.error(err?.message || 'Грешка при прихватању', { position: 'bottom-center' });
      } finally {
        setModeratorLoading(null);
      }
    },
    [relatedEntityId, notificationId, moderatorLoading, busy, onResolved, onModeratorAssignmentChanged]
  );

  const handleRejectModeration = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!relatedEntityId || !notificationId || moderatorLoading || busy) return;
      setModeratorLoading('reject');
      try {
        // X-Role: MODERATOR — definisano policy-jem u endpointRolePolicy.js (allowFunctionalModerator: true)
        await ObligationService.rejectModeration(relatedEntityId);
        await onModeratorAssignmentChanged?.(relatedEntityId);
        await NotificationService.toggleRead(notificationId);
        toast.success('Одбили сте улогу moderatora.', { position: 'bottom-center' });
        scheduleNotificationCountRefresh();
        onResolved?.(notificationId);
      } catch (err) {
        toast.error(err?.message || 'Грешка при одбијању', { position: 'bottom-center' });
      } finally {
        setModeratorLoading(null);
      }
    },
    [relatedEntityId, notificationId, moderatorLoading, busy, onResolved, onModeratorAssignmentChanged]
  );

  return {
    busy,
    moderatorLoading,
    handleMarkRead,
    handleAcceptModeration,
    handleRejectModeration,
  };
}
